import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, ViewChild } from "@angular/core";
import { Connection } from "@common/ADAPT.Common.Model/organisation/connection";
import { RoleConnection } from "@common/ADAPT.Common.Model/organisation/role-connection";
import { Team } from "@common/ADAPT.Common.Model/organisation/team";
import { Person } from "@common/ADAPT.Common.Model/person/person";
import { Autobind } from "@common/lib/autobind.decorator/autobind.decorator";
import { CommonDataService } from "@common/lib/data/common-data.service";
import { RxjsBreezeService } from "@common/lib/data/rxjs-breeze.service";
import { AdaptCommonDialogService } from "@common/ux/adapt-common-dialog/adapt-common-dialog.service";
import { BaseComponent } from "@common/ux/base.component/base.component";
import { SelectPeopleComponent } from "@org-common/lib/directory-shared/select-people/select-people.component";
import { CommonTeamsService } from "@org-common/lib/teams/common-teams.service";
import isEqual from "lodash.isequal";
import { EMPTY, filter, forkJoin, switchMap } from "rxjs";
import { catchError, finalize, startWith, tap } from "rxjs/operators";

@Component({
    selector: "adapt-team-roles-pin-person-button",
    standalone: false,
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: "./team-roles-pin-person-button.component.html",
})
export class TeamRolesPinPersonButtonComponent extends BaseComponent implements OnInit {
    @Input({ required: true }) public team!: Team;
    @Output() public roleConnectionsAdded = new EventEmitter<Connection[]>();

    public selectedPeople: Person[] = [];
    public popupVisible = false;
    public saving = false;

    private teamMembers: Set<Person> = new Set<Person>();

    @ViewChild(SelectPeopleComponent) public selectPeopleComponent?: SelectPeopleComponent;

    public constructor(
        private commonDataService: CommonDataService,
        private commonDialogService: AdaptCommonDialogService,
        private commonTeamsService: CommonTeamsService,
        private rxjsBreezeService: RxjsBreezeService,
    ) {
        super();
    }

    @Autobind
    public teamPersonFilter(person: Person) {
        return !this.teamMembers.has(person);
    }

    public onPeopleChange(people: Person[]) {
        if (!isEqual(people, this.selectedPeople)) {
            this.selectedPeople = people;
        }
    }

    public ngOnInit() {
        this.rxjsBreezeService.entityTypeChanged(RoleConnection).pipe(
            startWith(undefined),
            filter((roleConnection) => !roleConnection || roleConnection.teamId === this.team.teamId),
            switchMap(() => this.updateData()),
            this.takeUntilDestroyed(),
        ).subscribe();
    }

    private async updateData() {
        const allTeamMembers = await this.commonTeamsService.promiseToGetTeamMemberRoleConnections(this.team, true, true);
        this.teamMembers = new Set(this.commonTeamsService.getUniqueRoleConnectionPeople(allTeamMembers));
        this.selectPeopleComponent?.reset();
    }

    @Autobind
    public onAddClicked() {
        if (this.selectedPeople.length === 0) {
            return EMPTY;
        }

        this.saving = true;

        const connections = this.selectedPeople
            .map((person) => person.getLatestConnection())
            .filter((connection) => !!connection);
        return forkJoin(connections.map((connection) => this.commonTeamsService.promiseToAddTeamInput(this.team, connection))).pipe(
            switchMap((roleConnections) => this.commonDataService.saveEntities(roleConnections).pipe(
                catchError(() => this.commonDialogService.handleSaveFailure(roleConnections)),
            )),
            tap(() => {
                this.roleConnectionsAdded.emit(connections);
                this.cancel();
            }),
            switchMap(() => this.updateData()),
            finalize(() => this.saving = false),
        );
    }

    public cancel() {
        this.popupVisible = false;

        // clear selection when cancelling
        this.selectPeopleComponent?.reset();
    }
}
